import React from 'react'
import { leagueAttendanceStore } from 'state/league/leagueAttendance'
import { leagueEventStore } from 'state/league/leagueEvent'
import { useGetDropInHistory } from 'api/league/LeagueAttendance/leagueAttendance'
import { convertToUtc } from 'helper/utility'
import Spinner from 'components/Spinner'
import moment from 'moment'

const fmtTime = (iso: string | null | undefined) =>
  iso ? moment(iso).local().format('hh:mm A') : '—'

const fmtDate = (iso: string | null | undefined) =>
  iso ? moment(iso).local().format('MMM DD, YYYY') : '—'

const personLabel = (record: any, side: 'in' | 'out'): string => {
  if (side === 'in') {
    if (record.checkInAdmin) return 'Admin'
    if (record.checkInOrganizer)
      return `${record.checkInOrganizer.firstName} ${record.checkInOrganizer.lastName}`
    return 'Unknown'
  }
  if (record.checkOutAdmin) return 'Admin'
  if (record.checkOutOrganizer)
    return `${record.checkOutOrganizer.firstName} ${record.checkOutOrganizer.lastName}`
  return 'Unknown'
}

const DropInHistoryTab: React.FC = () => {
  const { selectedAttendee, selectedDate } = leagueAttendanceStore(store => ({
    selectedAttendee: store.selectedAttendee,
    selectedDate: store.selectedDate,
  }))
  const { leagueData } = leagueEventStore(store => ({ leagueData: store.leagueData }))

  const memberId = selectedAttendee?._id || ''
  const leagueId = leagueData?._id || ''
  const date = convertToUtc(selectedDate)

  const { data, isLoading, isFetching } = useGetDropInHistory(memberId, leagueId, date)

  const checkIns: any[] = data?.checkIns || []

  if (isLoading || isFetching) {
    return (
      <div className="flex justify-center py-40px">
        <Spinner />
      </div>
    )
  }

  if (!checkIns.length) {
    return <p className="py-20px text-14">No history found for this date.</p>
  }

  return (
    <div className="mt-20px">
      <h3 className="text-13 w-600 mb-20px">History</h3>

      {checkIns.map((record: any, index: number) => {
        const hasCheckOut = record.checkedOutAt != null

        return (
          <div
            key={record._id || index}
            className="border-btm pb-20px mb-20px"
          >
            {/* Check-in row */}
            {record.checkedInAt && (
              <div className="flex justify-between align-center mb-10px">
                <div>
                  <p className="text-12 opacity-60">Person check-in</p>
                  <p className="text-14 w-600 mt-4px">{personLabel(record, 'in')}</p>
                </div>
                <div
                  className="radius-8 border-1px flex flex-col align-center justify-center"
                  style={{ width: 90, height: 66, padding: '6px 8px' }}
                >
                  <p className="text-10 opacity-60 mb-4px">Check-in</p>
                  <p className="text-18 w-500">{fmtTime(record.checkedInAt)}</p>
                </div>
              </div>
            )}

            {/* Check-in date (if different from selected date) */}
            {record.checkedInAt && (
              <p className="text-12 opacity-60 mb-10px">
                {fmtDate(record.checkedInAt)}
              </p>
            )}

            {/* Check-out row */}
            {hasCheckOut && (
              <div className="flex justify-between align-center mt-10px">
                <div>
                  <p className="text-12 opacity-60">Person check-out</p>
                  <p className="text-14 w-600 mt-4px">{personLabel(record, 'out')}</p>
                </div>
                <div
                  className="radius-8 border-1px flex flex-col align-center justify-center"
                  style={{ width: 90, height: 66, padding: '6px 8px' }}
                >
                  <p className="text-10 opacity-60 mb-4px">Check-out</p>
                  <p className="text-18 w-500">{fmtTime(record.checkedOutAt)}</p>
                </div>
              </div>
            )}

            {/* Status badge */}
            <div className="mt-10px">
              <span
                className="text-12 px-10px py-4px radius-8"
                style={{
                  background:
                    record.checkOutStatus === 'dropped-out' ? '#FFE2E0'
                    : record.checkInStatus === 'droped-in' ? '#E8E9F7'
                    : '#F6F7F8',
                  color: '#000',
                }}
              >
                {record.checkOutStatus === 'dropped-out'
                  ? 'Dropped-out'
                  : record.checkInStatus === 'droped-in'
                  ? 'Dropped-in'
                  : record.checkInStatus || 'No status'}
              </span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default DropInHistoryTab
